// @flow

import nearestPointOnLine from '@turf/nearest-point-on-line';
import LngLat from '../geo/lng_lat';
import type {SnapKey, SnapMode, ViaPointModifierKey} from '../util/enums';
import {Common} from '../util/enums';
import {Event} from '../util/evented';
import {bindAll, warnOnce} from '../util/util';
import {MapMouseEvent, MapTouchEvent} from './events';
import type Map from './map';
import type Route from './route';
import {Search} from '../services/search';

/**
 * @typedef {Object} DragRouteOptions
 * @property {Common.SnapMode} snapMode
 * @property {Common.SnapKey} snapKey
 * @property {Common.ViaPointModifierKey} viaPointModifierKey
 */
export type DragRouteOptions = {
    snapMode: SnapMode;
    snapKey: SnapKey;
    viaPointModifierKey: ?ViaPointModifierKey;
}

export const defaultDragRouteOptions: DragRouteOptions = {
    snapMode: Common.SnapMode.ENABLE_WITH_KEY,
    snapKey: Common.SnapKey.SHIFT,
    viaPointModifierKey: null,
};

export class DragRouteHandler {
    _roadLayerNames: Array<string>;
    _highlightColor: string;
    _snapDistance: number;

    _isEnabled: boolean;

    _map: Map;
    _route: Route;
    _options: DragRouteOptions;

    _stopLayerFeature: Object;
    _routeLayerFeature: Object;

    _useSnappedLocation: boolean;
    _snappedLocation: LngLat | null;

    _dragSourceName: string;
    _dragLayerName: string;
    _isDragged: boolean;

    constructor(route: Route) {
        this._roadLayerNames = [
            'road_interstate',
            'road_ramp',
            'road_divided',
            'road_primary',
            'road_secondary',
            'road_local',
            'road_sublocal',
            'tunnel_minor',
            'tunnel_secondary',
            'tunnel_primary',
            'tunnel_divided',
            'tunnel_ramp',
            'tunnel_interstate',
            'customroad-customroad_all',
            'places_point',
            'places_point_basic',
            'privateplaces-privateplaces_all',
            'privateplaces-privateplaces_gates',
            'privateplaces-privateplaces_sites',
        ];
        this._highlightColor = '#FFFF99';
        this._snapDistance = 20; // pixels

        this._route = route;
        this._isEnabled = this._route._options.isDraggable;
        this._options = this._route._options.dragOptions;

        this._useSnappedLocation = false;
        this._dragSourceName = `drag-source-${this._route._routeId}`;
        this._dragLayerName = `drag-layer-${this._route._routeId}`;

        bindAll([
            '_addRouteDragHandler',
            '_addStopsDragHandler',
            '_onStopUp',
            '_onUp',
            '_onDrag',
            '_onMouseEnter',
            '_onMouseLeave',
            '_onMouseMoveRouteHandler',
            '_onMouseMoveStopHandler'
        ], this);
    }

    enable() {
        if (!this._hasMap()) return;

        this._isEnabled = true;

        this._addDragLayerAndSource();

        this._map.on('mousedown', this._route._stopLayerName, this._addStopsDragHandler);
        this._map.on('touchstart', this._route._stopLayerName, this._addStopsDragHandler);
        this._map.on('mousedown', this._route._routeLayerName, this._addRouteDragHandler);
        this._map.on('touchstart', this._route._routeLayerName, this._addRouteDragHandler);
        this._map.on('mouseenter', this._route._routeLayerName, this._onMouseEnter);
        this._map.on('mouseleave', this._route._routeLayerName, this._onMouseLeave);
        this._map.on('mousemove', this._route._routeLayerName, this._onMouseMoveRouteHandler);
        this._map.on('mousemove', this._route._stopLayerName, this._onMouseMoveStopHandler);
    }

    disable() {
        if (!this._hasMap()) return;

        this._isEnabled = false;

        this._map.off('mousedown', this._route._stopLayerName);
        this._map.off('touchstart', this._route._stopLayerName);
        this._map.off('mousedown', this._route._routeLayerName);
        this._map.off('touchstart', this._route._routeLayerName);
        this._map.off('mouseenter', this._route._routeLayerName);
        this._map.off('mouseleave', this._route._routeLayerName);
        this._map.off('mousemove', this._route._routeLayerName);

        if (this._map.getLayer(this._dragLayerName)) this._map.removeLayer(this._dragLayerName);
        if (this._map.getSource(this._dragSourceName)) this._map.removeSource(this._dragSourceName);
    }

    isEnabled() {
        return this._isEnabled;
    }

    _hasMap() {
        if (!this._route._map) {
            warnOnce('The route needs to be added to a map before drag routing can be enabled.');
            return false;
        } else {
            if (this._map !== this._route._map) this._map = this._route._map;
        }

        return true;
    }

    _addDragLayerAndSource() {
        if (!this._map.getSource(this._dragSourceName)) {
            this._map.addSource(this._dragSourceName, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: []
                }
            });
        }

        if (!this._map.getLayer('places-highlighted')) {
            this._map.addLayer({
                id: 'places-highlighted',
                type: 'circle',
                source: 'Basic_Places',
                'source-layer': 'places',
                paint: {
                    'circle-radius': 15,
                    'circle-color': this._highlightColor,
                },
                //Display none by adding a filter with an empty string.
                'filter': ['in', 'tpid', '']
            },
            );
        }

        this._map.once('customplaces', () => {
            if (this._map.getSource('PrivatePlaces') && !this._map.getLayer('privateplaces-highlighted')) {
                this._map.addLayer({
                    id: 'privateplaces-highlighted',
                    type: 'circle',
                    source: 'PrivatePlaces',
                    'source-layer': 'places',
                    paint: {
                        'circle-radius': 15,
                        'circle-color': this._highlightColor,
                    },
                    //Display none by adding a filter with an empty string.
                    'filter': ['in', 'tpid', '']
                },
                );
            }
        });

        if (!this._map.getLayer(this._dragLayerName)) {
            this._map.addLayer({
                id: this._dragLayerName,
                type: "line",
                source: this._dragSourceName,
                layout: {
                    "line-join": "round",
                    "line-cap": "round"
                },
                paint: {
                    "line-color": this._highlightColor,
                    // $FlowFixMe
                    "line-width": {
                        "stops": [
                            [ 10, 9 ],
                            [ 15, 11 ],
                            [ 19, 13 ]
                        ]
                    },
                    "line-opacity": 0.9,
                    "line-offset": 0
                }
            });
        }
    }

    // Events
    _onMouseEnter() {
        if (this._isEnabled) {
            this._map.getCanvas().style.cursor = 'move';
        }
    }

    _onMouseLeave() {
        if (this._isEnabled) {
            this._map.getCanvas().style.cursor = '';
        }
        this._removePointHandle();
    }

    _onMouseMoveRouteHandler(e: MapMouseEvent | MapTouchEvent) {
        this._addPointHandle(e.lngLat.lng, e.lngLat.lat);
    }

    _addPointHandle(lng: number, lat: number) {
        if (this._isEnabled) {
            const point = {
                "type": "FeatureCollection",
                "features": [{
                    "type": "Feature",
                    "properties": {"title": 'Drag to change route'},
                    "geometry": {
                        "type": "Point",
                        "coordinates": [lng, lat]
                    }
                }]
            };

            if (!this._map.getLayer('points')) {
                this._map.addSource('point', {
                    "type": "geojson",
                    "data": point,
                });

                this._map.addLayer({
                    "id": "points",
                    "source": "point",
                    "type": "circle",
                    paint: {
                        'circle-radius': 5,
                        'circle-color': '#00ffff',
                        'circle-stroke-color': '#000000',
                        'circle-stroke-opacity': 1,
                        'circle-stroke-width': 1,
                        'circle-opacity': 1
                    }
                });

                this._map.addLayer({
                    "id": "points-label",
                    "type": "symbol",
                    "source": "point",
                    "layout": {
                        "text-field": "{title}",
                        "text-size": 15,
                        "text-anchor": "top"
                    }
                });
            } else {
                if (point && point.features) {
                    point.features[0].geometry.coordinates = [lng, lat];
                    this._map.getSource('point').setData(point);
                }
            }
        }

    }
    _removePointHandle() {
        if (this._map.getLayer('points-label')) this._map.removeLayer('points-label');
        if (this._map.getLayer('points')) this._map.removeLayer('points');
        if (this._map.getSource('point')) this._map.removeSource('point');
    }
    _onMouseMoveStopHandler() {
        this._removePointHandle();
    }

    _addStopsDragHandler(e: MapMouseEvent | MapTouchEvent) {
        if (!e._defaultPrevented && this._isEnabled) {
            this._routeLayerFeature = null;
            const viaPointKey = this._options.viaPointModifierKey;
            // $FlowFixMe
            if (viaPointKey !== null && e.originalEvent[viaPointKey] === true) {
                // $FlowFixMe
                const feature = e.features[0];
                if (feature.properties.isViaPoint === true) {
                    const index = parseInt(feature.properties.stopLabel);
                    const viaPoints = this._route._getStopStatusViaPointFlags();
                    this._route._options.stops.splice(index, 1);
                    // remove the stop with via point flag
                    viaPoints.splice(index, 1);
                    this._route._getRoute(this._route._options, viaPoints);
                }
            } else {
                // $FlowFixMe
                this._stopLayerFeature = e.features; // Dynamic property added by map. _createDelegatedListener function.
                this._map.once('mouseup', this._onStopUp);
                this._map.once('touchend', this._onStopUp);
                this._onDragStart();
            }
        }
        e.preventDefault();
    }

    _onStopUp(e: MapMouseEvent | MapTouchEvent) {
        const newStopPromise = this._onDragEnd(e.lngLat);
        newStopPromise.then((newStop) => {
            if (newStop === null) return;
            if (this._stopLayerFeature[0] !== undefined) {
                if (this._stopLayerFeature[0].properties.stopType === 'start') {
                    this._route._options.stops[0] = newStop;
                }
                if (this._stopLayerFeature[0].properties.stopType === 'end') {
                    this._route._options.stops[this._route._options.stops.length - 1] = newStop;
                }
                if (this._stopLayerFeature[0].properties.stopType === 'stop') {
                    const index = parseInt(this._stopLayerFeature[0].properties.stopLabel);
                    this._route._options.stops[index] = newStop;
                }
            }
            if (this._route._undoRedoStack !== undefined && this._route._undoRedoStack !== null) {
                this._route._undoRedoStack.push([...this._route._options.stops]);
            }
            // restore via point flag for each stop
            const viaPoints = this._route._getStopStatusViaPointFlags();
            this._route._getRoute(this._route._options, viaPoints);
            this._isDragged = false;
        });
    }

    _addRouteDragHandler(e: MapMouseEvent | MapTouchEvent) {
        if (!e._defaultPrevented && this._isEnabled) {
            this._stopLayerFeature = null;
            const viaPointKey = this._options.viaPointModifierKey;
            // $FlowFixMe
            if (viaPointKey === null || e.originalEvent[viaPointKey] !== true) {
                this._route._routeLegPositions = [];
                const coords = e.lngLat;
                const point = {
                    "type": "Feature",
                    "geometry": {
                        "type": "Point",
                        "coordinates": coords.toArray()
                    }
                };
                if (this._route._routeSourceData) {
                    const legList = [];
                    for (let i = 0; i < this._route._routeSourceData.geometry.coordinates.length; i++) {
                        const eachLine = {
                            "type": "Feature",
                            "geometry": {
                                "type": "LineString",
                                "coordinates": this._route._routeSourceData.geometry.coordinates[i]
                            }
                        };
                        const eachDistance = nearestPointOnLine(eachLine, point);
                        eachDistance.properties._routeLegPosition = i; // attach to the properties object temporarily
                        legList.push(eachDistance.properties);
                    }
                    legList.sort((a, b) => (a.dist > b.dist) ? 1 : -1);
                    // find route legs are hited, by removing legs that are far from the mouse down location.
                    for (let i = legList.length - 1; i >= 0; i--) {
                        if (legList[i].dist - legList[0].dist > 0.001) { // 0.001 is one meter
                            legList.splice(i, 1);
                        }
                    }
                    legList.forEach(item => this._route._routeLegPositions.push(item._routeLegPosition));
                }
                this._map.once('mouseup', this._onUp);
                this._map.once('touchend', this._onUp);
                this._onDragStart();
            }
            // $FlowFixMe
            this._routeLayerFeature = e.features;
        }
        e.preventDefault();
    }

    _onUp(e: MapMouseEvent | MapTouchEvent) {
        this._removePointHandle();
        const coordsPromise = this._onDragEnd(e.lngLat);
        if (!this._isDragged) return;
        coordsPromise.then((coords) => {
            if (coords === null) return;

            const newStop = new LngLat(coords.lng, coords.lat);
            const data = {newStop, routeLegPositions: this._route._routeLegPositions};
            const stopInsertingEvent = new Event('stopInserting', data);
            const hasListener = this._route.listens('stopInserting'); // listener added by on or once.

            // Event documentation is in Route class for visibility
            this._route.fire(stopInsertingEvent);
            if (hasListener !== true) {
                if (this._route._routeLegPositions.length > 0) {
                    const routeLegPosition = this._route._routeLegPositions[0];
                    this._route.getRouteWithNewStop(newStop, routeLegPosition);
                } else {
                    warnOnce(`The API can't get available route leg positions for stop insertion. Make sure route souce data has been added.`);
                }
            }
        });
    }

    _onDragStart() {
        // start listening to mouse movement
        this._isDragged = false;
        this._map.on('mousemove', this._onDrag);
        this._map.on('touchmove', this._onDrag);
    }

    _getRoadLayerNames(): Array<string> {
        const layers = [];
        this._roadLayerNames.forEach((l) => {
            if (this._map.getLayer(l)) {
                layers.push(l);
            }
        });
        return layers;
    }

    _onDrag(e: MapMouseEvent | MapTouchEvent) {
        this._isDragged = true;
        this._clearFilters();
        // Allow stop to navigate along with cursor
        const current = [e.lngLat.lng, e.lngLat.lat];
        if (this._stopLayerFeature) {
            const stopType = this._stopLayerFeature[0].properties.stopType;
            const stopLabel = this._stopLayerFeature[0].properties.stopLabel;
            const stopFeatures = this._map.queryRenderedFeatures({
                layers: [this._route._stopLayerName]});
            if (stopFeatures && stopType) {
                const stopfeature = stopFeatures.find(feature => (feature.properties.stopType === stopType) && (feature.properties.stopLabel === stopLabel));
                if (stopfeature) {
                    stopfeature.geometry.coordinates = current;
                    this._map.getSource(this._route._stopSourceName).setData({
                        type: 'FeatureCollection',
                        features: stopFeatures
                    });
                }
            }

        } else if (this._routeLayerFeature) {
            this._addPointHandle(e.lngLat.lng, e.lngLat.lat);
        }

        // if snapMode is off return
        if (this._options.snapMode === Common.SnapMode.OFF) return;

        // Flow has an issue with indexing into the DOM events
        if (e.originalEvent && // $FlowFixMe
            ((this._options.snapMode === Common.SnapMode.ENABLE_WITH_KEY && e.originalEvent[this._options.snapKey]) || // $FlowFixMe
             (this._options.snapMode === Common.SnapMode.DISABLE_WITH_KEY && !e.originalEvent[this._options.snapKey]))) {

            this._useSnappedLocation = true;

            // check for closest visible road
            const closestPoint = this._nearestPointOnVisibleRoad(e.lngLat);

            if (closestPoint.properties.dist > 0) {
                // highlight matching segment
                const roads = this._map.queryRenderedFeatures(
                    this._map.project(closestPoint.geometry.coordinates),
                    {
                        layers: this._getRoadLayerNames()
                    }
                );
                if (roads.length > 0) {
                    if (roads.find(() => {
                        return true;
                    }).geometry.type === 'Point') {
                        const basicplaces =  roads.filter(item => item.layer.id === "places_point_basic");
                        const privateplaces =  roads.filter(item => item.layer.id === "privateplaces-privateplaces_all");
                        // eslint-disable-next-line camelcase
                        const tpid_basic = basicplaces.map(
                            (feature) => feature.properties.tpid
                        );
                        // eslint-disable-next-line camelcase
                        const tpid_private = privateplaces.map(
                            (feature) => feature.properties.tpid
                        );

                        if (this._map.getLayer('places-highlighted')) {
                            this._map.setFilter('places-highlighted', [
                                'in',
                                'tpid',
                                // eslint-disable-next-line camelcase
                                ...tpid_basic
                            ]);
                        }

                        if (this._map.getLayer('privateplaces-highlighted')) {
                            this._map.setFilter('privateplaces-highlighted', [
                                'in',
                                'tpid',
                                // eslint-disable-next-line camelcase
                                ...tpid_private
                            ]);
                        }

                    }
                    const roadData = {
                        type: 'FeatureCollection',
                        features: [
                            {
                                type: 'Feature',
                                properties: {
                                },
                                geometry: {
                                    type: 'LineString',
                                    coordinates: roads[0].geometry.coordinates
                                }
                            }
                        ]
                    };

                    this._map.getSource(this._dragSourceName).setData(roadData);
                }

                this._map.getCanvas().style.cursor = 'cell';
                // save location
                this._snappedLocation = LngLat.convert(closestPoint.geometry.coordinates);

            } else {
                // no matching roads
                // show icon
                this._map.getCanvas().style.cursor = 'not-allowed';
                // clear saved location
                this._snappedLocation = null;

                this._map.getSource(this._dragSourceName).setData({
                    type: 'FeatureCollection',
                    features: []
                });

            }

        } else {
            this._map.getCanvas().style.cursor = 'move';
            this._useSnappedLocation = false;
            this._snappedLocation = null;
            this._map.getSource(this._dragSourceName).setData({
                type: 'FeatureCollection',
                features: []
            });
        }

    }

    _onDragEnd(lngLat: LngLat): Promise<LngLat> {
        //stop listening to mouse movement
        this._map.off('mousemove', this._onDrag);
        this._map.off('touchmove', this._onDrag);

        // reset cursor
        this._map.getCanvas().style.cursor = '';
        // clear any highlighting
        this._map.getSource(this._dragSourceName).setData({
            type: 'FeatureCollection',
            features: []
        });
        this._clearFilters();

        let trimblePlaceId;
        let firstFeature;
        let featureTypePoint;
        const features = this._map.queryRenderedFeatures(
            this._map.project(lngLat),
            {
                layers: this._getRoadLayerNames()
            }
        );

        if (features && features.length) {
            firstFeature = features.find(() => {
                return true;
            });
            trimblePlaceId  = firstFeature.properties.tpid;
            featureTypePoint = features.filter(item => item.geometry.type === 'Point').find(() => {
                return true;
            });
        }
        if (this._useSnappedLocation && this._snappedLocation !== null) {
            // Using single search to reverse geocode because of the StreetCoords option
            // This should be refactored once we add single search to the services
            const region = this._map._region.toUpperCase() || 'NA';
            const searchLat = (this._snappedLocation !== null) ? this._snappedLocation.lat : lngLat.lat;
            const searchLng = (this._snappedLocation !== null) ? this._snappedLocation.lng : lngLat.lng;
            const searchPromise = Search.singleSearch({
                region,
                query: `${searchLat},${searchLng}`,
                include: 'Meta',
                maxResults: 1,
                includeTrimblePlaceIds: true
            });
            return searchPromise.then((resp) => {
                if (resp.Locations) {
                    const filterPlaceId =  resp.Locations.filter(item => item.ResultType === 17 && item.TrimblePlaceId === trimblePlaceId).find(() => {
                        return true;
                    });
                    const filterResType = resp.Locations.filter(item => item.ResultType === 17).find(() => {
                        return true;
                    });
                    const place = filterPlaceId ? filterPlaceId : filterResType;
                    const firstLocation = resp.Locations.find(() => {
                        return true;
                    });
                    if (place && place.Coords) {
                        return Promise.resolve(new LngLat(place.Coords.Lon, place.Coords.Lat));
                    } else
                    if (!featureTypePoint && resp.Locations && firstLocation && firstLocation.StreetCoords) {
                        return Promise.resolve(new LngLat(firstLocation.StreetCoords.Lon, firstLocation.StreetCoords.Lat));
                    } else {
                    // Fall back if there is an issue with geocoding
                        return Promise.resolve(new LngLat(searchLng, searchLat));
                    }
                } else {
                    // Fall back if there is an issue with geocoding
                    return Promise.resolve(new LngLat(searchLng, searchLat));
                }
            });
        } else {
            return Promise.resolve(lngLat);
        }
    }

    _clearFilters() {
        if (this._map.getLayer('places-highlighted')) {
            this._map.setFilter('places-highlighted', [
                'in',
                'tpid',
                ''
            ]);
        }
        if (this._map.getLayer('privateplaces-highlighted')) {
            this._map.setFilter('privateplaces-highlighted', [
                'in',
                'tpid',
                ''
            ]);
        }
    }
    _nearestPointOnVisibleRoad(lngLat: LngLat) {
        // normalize to LngLat array
        const location = LngLat.convert(lngLat).toArray();

        const locationFeature = {
            type: 'Feature',
            properties: {
                dist: 0
            },
            geometry: {
                type: 'Point',
                coordinates: location
            }
        };

        // queryRenderedFeatures takes points, not lngLat
        const searchPoint = this._map.project(location);

        const searchArea = [
            [searchPoint.x - this._snapDistance, searchPoint.y + this._snapDistance],
            [searchPoint.x + this._snapDistance, searchPoint.y - this._snapDistance]
        ];

        // Get the features of the visible roads nearby
        const roads = this._map.queryRenderedFeatures(
            searchArea,
            {
                layers: this._getRoadLayerNames()
            }
        );

        // No roads in search area, return a feature at the original location with dist of zero
        if (roads.length === 0) return locationFeature;

        locationFeature.properties.dist = 99999;

        const closestFeature = roads.reduce((closest, roadFeature) => {
            const nearestPointFeature = nearestPointOnLine(roadFeature.geometry, location);

            return (nearestPointFeature.properties.dist < closest.properties.dist) ? nearestPointFeature : closest;
        }, locationFeature);

        return closestFeature;
    }

}
